import { logger } from './Logger';
import { Common } from './Common';
import { stringJson as stringJsonUtil, parseJson as parseJsonUtil } from './json-utils';

declare var WorkerGlobalScope: any;
declare var process: any;
declare var global: any;

/**
 * Przydatne funkcje
 *
 * @param  {object} $q Proimse
 * @return {object}    Factory
 */
export class Core {
    constructor() {
        'ngInject';
    }

    /**
     * Wyszukuje index elementu w tablicy o zadanych parametrach (domyślnie id).
     * @param {object[]} array Tablica do przeszukania.
     * @param {int|int[]} id Szukane id.
     * @param {string|string[]} [prop='id'] Pole, w którym jest id.
     * @returns {*} element tablicy
     */
    fId<T extends any[]>(array: T, id: any | any[], prop: (keyof T[0]) | (keyof T[0])[] = 'id') {
        prop = prop || 'id';
        if (Common.isDefined(array) && Array.isArray(array)) {
            for (let i = 0; i < array.length; i++) {
                if (array[i] !== null && Common.isObject(array[i])) {
                    if (Common.isString(prop) && !Array.isArray(id) && array[i][prop] === id) {
                        return i;
                    } else if (
                        Array.isArray(prop)
                        && Array.isArray(id)
                        && prop.length === id.length
                    ) {
                        let allMatch = true;
                        for (let j = 0; j < prop.length; j++) {
                            if (array[i][prop[j]] !== id[j]) {
                                allMatch = false;
                                break;
                            }
                        }
                        if (allMatch) {
                            return i;
                        }
                    }
                }
            }
            return undefined;
        }
        return undefined;
    }

    /**
     * Wyszukuje element w tablicy o zadanym id.
     * @param {object[]} array Tablica do przeszukania.
     * @param {int} id Szukane id.
     * @param {string} [prop='id'] Pole, w którym jest id.
     * @returns {*} element obiektu
     */
    fIdO<T extends { id: string | number }>(array: T[], id, prop: keyof T = 'id') {
        prop = prop || 'id';
        if (Common.isDefined(array) && Array.isArray(array)) {
            for (let i = 0; i < array.length; i++) {
                if (
                    array[i] !== null
                    && Common.isObject(array[i])
                    && ((!isNaN(Number(array[i][prop])) && Number(array[i][prop]) === Number(id))
                        || array[i][prop] === id)
                ) {
                    return array[i];
                }
            }
            return undefined;
        }
        return undefined;
    }

    /**
     * Usuń element z tablicy na podstawie jego id
     * @param {object[]} array Tablica do przeszukania.
     * @param {int} id Szukane id.
     * @param {string} [prop='id'] Pole, w którym jest id.
     */
    removeById(array: any[], id, prop = 'id') {
        if (Common.isDefined(array) && Array.isArray(array)) {
            for (let i = 0; i < array.length; i++) {
                if (array[i][prop] === id) {
                    array.splice(i, 1);
                }
            }
        }
    }

    /**
     * Zawsze zwraca objekt JSONa, nawet w przypadku błędnego stringa
     * @param {string} [str] Tekst zawierający JSON
     * @returns {object} Objekt
     */
    parseJson(str, allowStrings = false) {
        if (str && typeof str === 'object' && str !== null) {
            return str;
        } else {
            try {
                if (allowStrings && ['{', '['].indexOf(str.charAt(0)) === -1) {
                    return str;
                } else {
                    const o = JSON.parse(str);
                    if (o && typeof o === 'object' && o !== null) {
                        return o;
                    } else if (o && typeof o === 'string') {
                        return this.parseJson(o);
                    }
                }
            } catch (e) {
                const position = e.message.match(/position\s(\d+)/);
                logger.log(
                    'Nie można rozpoznać JSON w stringu. :'
                        + (str
                            && str
                                .toString()
                                .substr(position && position[1] ? position[1] - 20 : 0, 100))
                );
            }
            return {};
        }
    }

    /**
     * Zawsze zwraca string JSONa, nawet w przypadku błędnego objektu
     * @param {object} [obj] objekt/zmienna/tablica
     * @returns {string} JSON
     * @deprecated Use stringJsonUtil from json-utils.ts directly for new code
     */
    stringJson(obj) {
        return stringJsonUtil(obj);
    }

    /**
     * Zwraca zmienna jako liczbę
     * @param {mixed} number dowolna zmienna
     * @returns {Number} liczba
     */
    num(number) {
        if (Common.isUndefined(number)) {
            return 0;
        } else if (Number(number) === number) {
            return number;
        } else {
            return number * 1 || 0;
        }
    }

    /**
     * Przelicza wszystkie ceny w obiekcie mnożąc przez współczynnik
     * @param {object} elems obiekt do przeszukania
     * @param {number} factor wspolczynnik
     */
    countFactor(elems, factor) {
        if (Common.isObject(elems)) {
            for (const k in elems) {
                if (Common.isArray(elems[k])) {
                    for (let i = 0; i < elems[k].length; i++) {
                        if (Common.isObject(elems[k][i])) {
                            if (Common.isDefined(elems[k][i].price)) {
                                elems[k][i].price *= parseFloat(factor);
                            } else {
                                this.countFactor(elems[k][i], factor);
                            }
                        }
                    }
                } else if (Common.isObject(elems[k])) {
                    if (Common.isDefined(elems[k].price)) {
                        elems[k].price *= parseFloat(factor);
                    } else {
                        this.countFactor(elems[k], factor);
                    }
                } else if (k === 'price') {
                    if (Common.isDefined(elems[k])) {
                        (elems as any)[k] *= parseFloat(factor);
                    }
                }
            }
        }
    }

    /**
     * Zwraca elementy obiektu jako elementy tablicy
     * @param {object} obj obiekt do przeanalizowania
     * @return {array} tablica wartoś obiektow
     */
    objToArray(obj) {
        if (Common.isArray(obj)) {
            return obj;
        }
        if (Common.isObject(obj) && obj !== null) {
            const arr = [];
            Common.forEach(obj, el => arr.push(el));
            return arr;
        } else {
            return [];
        }
    }

    /**
     * Zaokragla zmienna do drugiego miejsca po przecinku
     * @param {object} n obiekt do przeszukania
     * @return {number} zaokraglona zmienna
     */
    round(n, points = 100) {
        if (isFinite(n)) {
            return Math.round(n * points) / points;
        } else {
            return 0;
        }
    }

    /**
     * Zaokragla cenę do ósmego miejsca po przecinku
     * @param {number} n cena do zaokrąglenia
     * @return {number} zaokraglona wartość
     */
    roundPrice(n) {
        return this.round(n, 100000000);
    }

    /**
     * Zwraca tablice licz z podanego zakresu
     * jeżeli stop < start zwraca pusta tablice, nie liczby od stop do start
     * @param {Number} [start] poczatek zakresu
     * @param {Number} [stop]  koniec zakresu
     * @return {Array} tablica zwroconych liczb
     */
    range(start, stop) {
        if (start > stop) {
            return [];
        } else {
            let a = [start],
                b = start;
            while (b < stop) {
                b += 1;
                a.push(b);
            }
            return a;
        }
    }

    /**
     * Wykonuje kopię obiektu (usuwa referencję) - szybsze niż angular.copy
     * @param  {object} obj Obiekt do skopiowania
     * @return {object}     Skopiowany obiekt
     */
    copy<T>(obj: T): T {
        if (typeof obj === 'object') {
            return this.parseJson(this.stringJson(obj));
        } else {
            return Common.deepCopy(obj);
        }
    }

    /**
     * Zapisuje fragment drzewa DOM do base64.
     * @param  {DOMObject} target Element DOM
     * @return {string}           Base64
     */
    svgToPngData(target) {
        if (Common.isDefined(target)) {
            let str = target.outerHTML;
            const regex = /ng\-[^"]*\s*=\s*"[^"]*"/gim;
            str = str.replace(regex, '');
            str = str.replace(/url\('https?:\/\/[^#'"]*#/g, "url('#");
            return btoa(
                encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function(match, p1) {
                    return String.fromCharCode(parseInt('0x' + p1));
                })
            );
            // window.btoa(decodeURIComponent(encodeURIComponent(target.outerHTML)));
        } else {
            return '';
        }
    }

    /**
     * Generuje UUID.
     * @return {string} UUID
     */
    generateUUID() {
        let d = new Date().getTime();
        const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = (d + Math.random() * 16) % 16 | 0;
            d = Math.floor(d / 16);
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
        return uuid;
    }

    /**
     * Dopełnia znakami "0" do podanej długości stringa
     * @param  {Number} a        Liczba oryginalna
     * @param  {Number} numZeros Oczekiwana liczba znaków
     * @return {String}          Dopełniony zerami cig znaków
     */
    zeroPad(a, numZeros) {
        if (a === '') {
            return '';
        }
        const n = Math.abs(a);
        if (isNaN(n)) {
            return a;
        } else {
            const zeros = Math.max(0, numZeros - Math.floor(n).toString().length);
            let zeroString = Math.pow(10, zeros)
                .toString()
                .substr(1);
            if (a < 0) {
                zeroString = '-' + zeroString;
            }
            return zeroString + n;
        }
    }

    /**
     * Sprawdza czy dany obiekt/tablica jest pusta lub ma wszystkie lementy puste
     * @param  {object}  o Sprawdzany obiekt
     * @return {Boolean}   true jezeli pusty
     */
    isEmpty(o) {
        if (Common.isUndefined(o) || o === null) {
            return true;
        } else if (Common.isObject(o) && !Object.keys(o).length) {
            return true;
        } else if (Common.isArray(o) && !o.length) {
            return true;
        } else if (Common.isArray(o) || Common.isObject(o)) {
            for (const k in o) {
                if (
                    !Common.isDefined(o[k])
                    || ((!Common.isArray(o[k]) || !o[k].length)
                        && (!Common.isObject(o[k]) || !Common.equals(o[k], {}))
                        && !Common.isString(o[k])
                        && !Common.isNumber(o[k]))
                ) {
                    return true;
                }
            }
            return false;
        } else {
            return false;
        }
    }

    /**
     * Sprawdza czy dany obiekt jest pusty
     * @param  {object}  o Sprawdzany obiekt
     * @return {Boolean}   true jezeli pusty
     */
    isEmptyObject(o) {
        if (Common.isUndefined(o) || o === null) {
            return true;
        } else if (Common.isObject(o) && !Object.keys(o).length) {
            return true;
        } else {
            return false;
        }
    }

    /**
     * Zwraca porzdek sortowania tablicy wg klucza
     * @param  {string} key  Nazwa klucza, po ktorym sortujemy
     * @param  {string} key2 Nazwa klucza kolejnego poziomu
     * @param  {bool} desc   porzadek sortowania, domyślnie rosnco
     * @return {bool}        Czy element ma być wcześniej, czy poźniej
     */
    sortByKey(key, key2, desc) {
        return (a, b) => {
            if (typeof key2 !== 'undefined' && key2 !== null) {
                return desc ? ~~a[key][key2] < ~~b[key][key2] : ~~a[key][key2] > ~~b[key][key2];
            } else {
                return desc ? ~~a[key] < ~~b[key] : ~~(a[key] > b[key]);
            }
        };
    }

    /**
     * Czyści tablicę.
     * @param  {array} array Tablica
     */
    clear(array) {
        for (let i = array.length - 1; i >= 0; i--) {
            array.pop();
        }
    }

    /**
     * Zwraca część wspólną dwóch tablic z liczbami.
     * @param  {array} a Tablica
     * @param  {array} b Tablica
     * @return {array}   Część wspólna
     */
    intersection(a, b) {
        a = a.sort((x, y) => x - y);
        b = b.sort((x, y) => x - y);

        let ai = 0,
            bi = 0;
        const result = [];

        while (ai < a.length && bi < b.length) {
            if (a[ai] < b[bi]) {
                ai++;
            } else if (a[ai] > b[bi]) {
                bi++;
            } else {
                result.push(a[ai]);
                ai++;
                bi++;
            }
        }

        return result;
    }

    /**
     * Przepisuje zawartość tablicy jednej do drugiej - nie robi kopii, nie przepisuje referencji.
     * @param  {array} a             Tablica, do której dane mają trafić
     * @param  {array} b             Tablica, z której dane mają zostac przekopiowane
     * @param  {bool}  [order=false] Porządek przepisywania, domyślnie rosnąco
     */
    rewriteArray(a, b, order = false) {
        let i;
        if (Common.isArray(a) && Common.isArray(b)) {
            if (order) {
                for (i = b.length - 1; i >= 0; i--) {
                    a.push(b[i]);
                }
            } else {
                for (i = 0; i < b.length; i++) {
                    a.push(b[i]);
                }
            }
        }
    }

    /**
     * Funkcja liczaca sume kontrolna crc32
     * @param  {string} str Ciąg znaków do sprawdzenia
     * @return {string}     Wartość sumy
     */
    crc32(str) {
        const table =
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
        let crc = 0;
        let x: any = 0;
        let y = 0;

        crc = crc ^ -1;
        for (let i = 0, iTop = str.length; i < iTop; i++) {
            y = (crc ^ str.charCodeAt(i)) & 0xff;
            x = '0x' + table.substr(y * 9, 8);
            crc = (crc >>> 8) ^ x;
        }

        return crc ^ -1;
    }

    /**
     * Zamiana liczby na wertosc szesnastkowa
     * @param  {number} number Liczba dziesietnie
     * @return {string}        Liczba szesnastkowo
     */
    decToHex(number) {
        if (number < 0) {
            number = 0xffffffff + number + 1;
        }
        return parseInt(number, 10).toString(16);
    }

    /**
     * Formatuje liczbę
     *
     * @method     formatNumber
     * @param      {number}   a     liczba
     * @param      {number}   n       długość ułamka
     * @param      {number}   x       długość grup całości
     * @param      {*}        s       separator grup
     * @param      {*}        c       separator ułamka
     * @return     {string}           Sformatowana liczba
     */
    formatNumber(a, n, x, s?, c?) {
        const re = '\\d(?=(\\d{' + (x || 3) + '})+' + (n > 0 ? '\\D' : '$') + ')';
        a = a.toFixed(Math.max(0, ~~n));
        return (c ? a.replace('.', c) : a).replace(new RegExp(re, 'g'), '$&' + (s || ','));
    }

    /**
     * Zwraca łańcuch znaków, który nie jest zaokrąglany i posiada tyle cyfr po separatorze
     * dziesiętnym, ile wskazuje na to wartość parametru {fixed}.
     *
     * @param number
     *
     * @param fixed         ilość miejsc po separatorze dziesiętnym
     * @returns {string}
     */
    toFixed(number, fixed) {
        const re = new RegExp('^-?\\d+(?:.\\d{0,' + (fixed || -1) + '})?');
        return number.toString().match(re)[0];
    }

    /**
     * Sprawdza czy zmienna jest nieujemna liczba.
     * @param  {*}  n Zmienna do sprawdzenia
     * @return {Boolean}   Czy zmienna jest nieujemna liczba.
     */
    isNumberAndNotZero(n) {
        return Common.isNumber(n) && n > 0;
    }

    /**
     * Konwertuje ciag znakw do typu binarnego
     * @param  {string} base64Data  Ciag znakow
     * @param  {string} contentType Typ MIME
     * @param  {string} filename    Nazwa pliku
     * @param  {string} position    Pozycja
     * @return {blob}               Obiekt binarny
     */
    stringToBlob(base64Data, contentType, filename, position) {
        contentType = contentType || '';
        const sliceSize = 1024;
        const byteCharacters = atob(base64Data);
        const bytesLength = byteCharacters.length;
        const slicesCount = Math.ceil(bytesLength / sliceSize);
        const byteArrays = new Array(slicesCount);

        for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
            const begin = sliceIndex * sliceSize;
            const end = Math.min(begin + sliceSize, bytesLength);

            const bytes = new Array(end - begin);
            for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
                bytes[i] = byteCharacters[offset].charCodeAt(0);
            }
            byteArrays[sliceIndex] = new Uint8Array(bytes);
        }
        const blob: any = new Blob(byteArrays, { type: contentType });
        blob.name = filename;
        blob.lastModifiedDate = new Date();
        if (position) {
            blob.position = position;
        }

        return blob;
    }

    /**
     * Ustawia pierwszą duża literę w tekście.
     *
     * @param {string} string Tekst
     * @return {string} Tekst z duża literą.
     */
    capitalize(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }

    /**
     * Decimal adjustment of a number.
     *
     * @param {string}  type  The type of adjustment.
     * @param {number}  value The number.
     * @param {number} exp   The exponent (the 10 logarithm of the adjustment base).
     * @returns {number} The adjusted value.
     */
    decimalAdjust(type, value, exp) {
        // If the exp is undefined or zero...
        if (typeof exp === 'undefined' || +exp === 0) {
            return Math[type](value);
        }
        value = +value;
        exp = +exp;
        // If the value is not a number or the exp is not an integer...
        if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
            return NaN;
        }
        // Shift
        value = value.toString().split('e');
        value = Math[type](+(value[0] + 'e' + (value[1] ? +value[1] - exp : -exp)));
        // Shift back
        value = value.toString().split('e');
        return +(value[0] + 'e' + (value[1] ? +value[1] + exp : exp));
    }

    /**
     * Decimal round of a number.
     *
     * @param {number}  value The number.
     * @param {number} exp   The exponent (the 10 logarithm of the adjustment base).
     * @returns {number} The rounded value.
     */
    round10(value, exp) {
        return this.decimalAdjust('round', value, exp);
    }

    /**
     * Wybiera wartość z obiektu na podstawie ścieżki.
     *
     * @param {object} obj  obiekt
     * @param {string} path ścieżka
     * @return {any} Wartość z obiektu
     */
    deepFind(obj, path, separator = '.') {
        if (path == null || '') {
            return undefined;
        }
        if (!Common.isString(path)) {
            path = path.toString();
        }
        const paths = path.split(separator);
        let current = obj;

        for (let i = 0; i < paths.length; ++i) {
            const p = paths[i];
            if (current[p] === undefined) {
                return undefined;
            } else {
                current = current[p];
            }
        }
        return current;
    }

    sha1(str) {
        const rotate_left = function(n, s) {
            const t4 = (n << s) | (n >>> (32 - s));
            return t4;
        };

        const cvt_hex = function(val) {
            let str = '';
            let i;
            let v;

            for (i = 7; i >= 0; i--) {
                v = (val >>> (i * 4)) & 0x0f;
                str += v.toString(16);
            }
            return str;
        };

        let blockstart;
        let i, j;
        const W = new Array(80);
        let H0 = 0x67452301;
        let H1 = 0xefcdab89;
        let H2 = 0x98badcfe;
        let H3 = 0x10325476;
        let H4 = 0xc3d2e1f0;
        let A, B, C, D, E;
        let temp;

        // str = this.utf8_encode(str);
        const str_len = str.length;

        const word_array = [];
        for (i = 0; i < str_len - 3; i += 4) {
            j =
                (str.charCodeAt(i) << 24)
                | (str.charCodeAt(i + 1) << 16)
                | (str.charCodeAt(i + 2) << 8)
                | str.charCodeAt(i + 3);
            word_array.push(j);
        }

        switch (str_len % 4) {
            case 0:
                i = 0x080000000;
                break;
            case 1:
                i = (str.charCodeAt(str_len - 1) << 24) | 0x0800000;
                break;
            case 2:
                i =
                    (str.charCodeAt(str_len - 2) << 24)
                    | (str.charCodeAt(str_len - 1) << 16)
                    | 0x08000;
                break;
            case 3:
                i =
                    (str.charCodeAt(str_len - 3) << 24)
                    | (str.charCodeAt(str_len - 2) << 16)
                    | (str.charCodeAt(str_len - 1) << 8)
                    | 0x80;
                break;
        }

        word_array.push(i);

        while (word_array.length % 16 !== 14) {
            word_array.push(0);
        }

        word_array.push(str_len >>> 29);
        word_array.push((str_len << 3) & 0x0ffffffff);

        for (blockstart = 0; blockstart < word_array.length; blockstart += 16) {
            for (i = 0; i < 16; i++) {
                W[i] = word_array[blockstart + i];
            }
            for (i = 16; i <= 79; i++) {
                W[i] = rotate_left(W[i - 3] ^ W[i - 8] ^ W[i - 14] ^ W[i - 16], 1);
            }

            A = H0;
            B = H1;
            C = H2;
            D = H3;
            E = H4;

            for (i = 0; i <= 19; i++) {
                temp =
                    (rotate_left(A, 5) + ((B & C) | (~B & D)) + E + W[i] + 0x5a827999)
                    & 0x0ffffffff;
                E = D;
                D = C;
                C = rotate_left(B, 30);
                B = A;
                A = temp;
            }

            for (i = 20; i <= 39; i++) {
                temp = (rotate_left(A, 5) + (B ^ C ^ D) + E + W[i] + 0x6ed9eba1) & 0x0ffffffff;
                E = D;
                D = C;
                C = rotate_left(B, 30);
                B = A;
                A = temp;
            }

            for (i = 40; i <= 59; i++) {
                temp =
                    (rotate_left(A, 5) + ((B & C) | (B & D) | (C & D)) + E + W[i] + 0x8f1bbcdc)
                    & 0x0ffffffff;
                E = D;
                D = C;
                C = rotate_left(B, 30);
                B = A;
                A = temp;
            }

            for (i = 60; i <= 79; i++) {
                temp = (rotate_left(A, 5) + (B ^ C ^ D) + E + W[i] + 0xca62c1d6) & 0x0ffffffff;
                E = D;
                D = C;
                C = rotate_left(B, 30);
                B = A;
                A = temp;
            }

            H0 = (H0 + A) & 0x0ffffffff;
            H1 = (H1 + B) & 0x0ffffffff;
            H2 = (H2 + C) & 0x0ffffffff;
            H3 = (H3 + D) & 0x0ffffffff;
            H4 = (H4 + E) & 0x0ffffffff;
        }

        temp = cvt_hex(H0) + cvt_hex(H1) + cvt_hex(H2) + cvt_hex(H3) + cvt_hex(H4);
        return temp.toLowerCase();
    }

    /**
     * Zamienia string na camelCase
     * @param {string} str String do zamiany
     * @return {string} Zamieniony string
     */
    toCamelCase(str) {
        return str.replace(/[-_ ]([a-z])/g, function(g) {
            return g[1].toUpperCase();
        });
    }

    toSnakeCase(str) {
        return str.replace(/([A-Z])/g, function(g) {
            return '_' + g.toLowerCase();
        });
    }

    /**
     * Zamienia tablicę obiektów na listę
     * @param arr Tablica obiektów
     * @param key Nazwa wartości klucza
     * @param value Nazwa wartości
     */
    arrayOfObjectsToList(arr = [], key = 'id', value = 'name') {
        const list = {};
        if (arr !== null) {
            for (let i = 0; i < arr.length; i++) {
                if (Common.isDefined(arr[i][key]) && Common.isDefined(arr[i][value])) {
                    list[arr[i][key]] = arr[i][value];
                }
            }
        }

        return list;
    }

    isWorker() {
        return typeof WorkerGlobalScope !== 'undefined' && self instanceof WorkerGlobalScope;
    }

    isNode() {
        return (
            typeof global !== 'undefined'
            && typeof process === 'object'
            && typeof process.versions === 'object'
            && typeof process.versions.node !== 'undefined'
        );
    }

    b64toBlob(b64Data, contentType = '', sliceSize = 512) {
        const byteCharacters = atob(b64Data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = slice.split('').map(c => c.charCodeAt(0));
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, { type: contentType });
        return blob;
    }

    // Sprawdzanie przecięcia się odcinków
    // Zwraca false jeśli się nie przecinają, 2 jeśli się przecinają, 1 jeśli koniec jednego z odcinków leży na drugim
    calcIsIntersected(start1, end1, start2, end2) {
        if (this.calcAbsoluteBelong(start1, end1, start2, end2)) {
            return 1;
        } else {
            if (
                this.calcDeterminant(start1, end1, start2)
                    * this.calcDeterminant(start1, end1, end2)
                    >= 0
                || this.calcDeterminant(start2, end2, start1)
                    * this.calcDeterminant(start2, end2, end1)
                    >= 0
            ) {
                return false;
            } else {
                return 2;
            }
        }
    }

    // Sprawdza czy koniec jednego z odcinków leży na drugim
    calcAbsoluteBelong(start1, end1, start2, end2) {
        return (
            this.calcBelongsToSection(start1, end1, start2)
            || this.calcBelongsToSection(start1, end1, end2)
            || this.calcBelongsToSection(start2, end2, start1)
            || this.calcBelongsToSection(start2, end2, end1)
        );
    }

    // Sprawdza czy punkt C leży na odcinku AB
    calcBelongsToSection(start, end, point) {
        const det =
            start.x * end.y
            + end.x * point.y
            + point.x * start.y
            - point.x * end.y
            - start.x * point.y
            - end.x * start.y;
        if (det !== 0) {
            return false;
        } else {
            if (
                Math.min(start.x, end.x) <= point.x
                && point.x <= Math.max(start.x, end.x)
                && Math.min(start.y, end.y) <= point.y
                && point.y <= Math.max(start.y, end.y)
            ) {
                return true;
            } else {
                return false;
            }
        }
    }

    // Liczy wyznacznik
    calcDeterminant(start, end, point) {
        return (
            start.x * end.y
            + end.x * point.y
            + point.x * start.y
            - point.x * end.y
            - start.x * point.y
            - end.x * start.y
        );
    }

    /**
     * Sprawdza czy punkt znajduje się w poligonie
     * @param  {array}  sizes Tablica punktów
     * @param  {number} x     Pozycja punktu w osi X
     * @param  {number} y     Pozycja punktu w osi Y
     */
    pointInPolygon(sizes, x, y) {
        if (sizes) {
            const sizesXY = sizes.map(size => ({
                x: Number(size.width),
                y: Number(size.height),
            }));
            return this.contain(sizesXY, x, y);
        } else {
            return false;
        }
    }

    getRangeFromPoly(poly: any[]) {
        const minX = Math.min.apply(Math, poly.map(o => o.x));
        const minY = Math.min.apply(Math, poly.map(o => o.y));

        const maxX = Math.max.apply(Math, poly.map(o => o.x));
        const maxY = Math.max.apply(Math, poly.map(o => o.y));

        return {
            minX,
            minY,
            maxX,
            maxY,
        };
    }

    contain(bounds, lat, lng) {
        // https://rosettacode.org/wiki/Ray-casting_algorithm
        let count = 0;
        let onBounds = false;
        for (let b = 0; b < bounds.length; b++) {
            const vertex1 = bounds[b];
            const vertex2 = bounds[(b + 1) % bounds.length];
            if (this.onLineSegment(vertex1, vertex2, lat, lng)) {
                onBounds = true;
            }
            if (this.west(vertex1, vertex2, lat, lng)) {
                ++count;
            }
        }

        return count % 2 !== 0 || onBounds;
    }

    west(A, B, x, y) {
        if (A.y > B.y) {
            return this.west(B, A, x, y);
        }
        if (y === B.y || y === A.y) {
            y -= 0.00001;
        }
        if (y < A.y || y > B.y || (x > A.x && x > B.x)) {
            return false;
        } else if (x < A.x && x < B.x) {
            return true;
        } else {
            return (y - A.y) / (x - A.x) >= (B.y - A.y) / (B.x - A.x);
        }
    }

    onLineSegment(A, B, x, y) {
        const d = Math.abs((B.x - A.x) * (A.y - y) - (A.x - x) * (B.y - A.y));
        const minX = Math.min(A.x, B.x);
        const maxX = Math.max(A.x, B.x);
        const minY = Math.min(A.y, B.y);
        const maxY = Math.max(A.y, B.y);
        return d < 1 && x >= minX && x <= maxX && y >= minY && y <= maxY;
    }

    indexToLetter(index: number) {
        return String.fromCharCode(64 + index);
    }

    opposite(side: 'top' | 'bottom' | 'left' | 'right') {
        switch (side) {
            case 'top':
                return 'bottom';
            case 'bottom':
                return 'top';
            case 'left':
                return 'right';
            case 'right':
                return 'left';
        }
    }

    flipHexString(hexValue, hexDigits) {
        let h = hexValue.substr(0, 2);
        for (let i = 0; i < hexDigits; ++i) {
          h += hexValue.substr(2 + (hexDigits - 1 - i) * 2, 2);
        }
        return h;
    }

    hexToFloat(hex) {
        const s = hex >> 31 ? -1 : 1;
        const e = (hex >> 23) & 0xFF;
        return s * (hex & 0x7fffff | 0x800000) * 1.0 / Math.pow(2, 23) * Math.pow(2, (e - 127))
    }

    dimensionUnit(s) {
        return Math.round(s) + ' mm';
    }

    isValueInRangeWithIncludedStepValue(min: number, max: number, step: number, value: number) {
        if(!value) {
            return null;
        }

        const values: number[] = [];
        while(min <= max) {
            values.push(min);
            min += step;
        }

        return values.includes(value);
    }

    isNumberInRange(value: number, min: number, max: number) {
        return value >= min && value <= max;
    }

    dd(sequence) {
        const s = this.parseJson(sequence);
        return s.length > 0 && s[0][Object.keys(s[0])[0]].length;
    }
}

export const core = new Core();
